import type { ProductionHistoryPremiumSource } from 'ag-common-lib/lib/contracts/reporting-api.contracts';

export type HistoryGranularity = Granularity;

export enum Granularity {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Annual = 'annual',
}

export type GranularityOption = {
  id: HistoryGranularity;
  text: string;
};

export const GRANULARITY_OPTIONS: GranularityOption[] = [
  { id: Granularity.Monthly, text: 'Monthly' },
  { id: Granularity.Quarterly, text: 'Quarterly' },
  { id: Granularity.Annual, text: 'Annual' },
];

export enum ProductionHorizon {
  Actual = 'actual',
  Projection = 'projection',
}

export enum ProductionProjectionModel {
  RunRate = 'runrate',
  Pace = 'pace',
  Credibility = 'credibility',
}

export type ProductionProjectionModelOption = {
  id: ProductionProjectionModel;
  text: string;
};

export const PRODUCTION_PROJECTION_MODEL_OPTIONS: ProductionProjectionModelOption[] = [
  { id: ProductionProjectionModel.RunRate, text: 'Run Rate' },
  { id: ProductionProjectionModel.Pace, text: 'Pace' },
  { id: ProductionProjectionModel.Credibility, text: 'Credibility' },
];

export const PRODUCTION_PROJECTION_MODEL_LABELS: Record<ProductionProjectionModel, string> = {
  [ProductionProjectionModel.RunRate]: 'Run Rate',
  [ProductionProjectionModel.Pace]: 'Pace',
  [ProductionProjectionModel.Credibility]: 'Credibility',
};

/** Actual Production keeps the current history view. Switch this to Projection only if that mode should load first. */
export const DEFAULT_PRODUCTION_HORIZON = ProductionHorizon.Actual;
export const DEFAULT_PRODUCTION_PROJECTION_MODEL = ProductionProjectionModel.RunRate;

export type ProductionHistoryDashboardGridRow = {
  label: string;
  priorPeriodChange: number | null;
  currentYearChange: number | null;
  priorPeriodChangeIsProjectionDerived?: boolean;
  currentYearChangeIsProjectionDerived?: boolean;
  annualRowKind?: 'production' | 'priorYearChange';
  valueKind?: 'currency' | 'percent';
  monthlyRankByYear?: Record<number, number>;
  globalTopMonthByYear?: Record<number, boolean>;
  quarterlyRankByYear?: Record<number, number>;
  globalTopQuarterByYear?: Record<number, boolean>;
  isProjectedByYear?: Record<number, boolean>;
  isProjectionHighlightedByYear?: Record<number, boolean>;
  premiumSourceByYear?: Record<number, ProductionHistoryPremiumSource>;
  isProjectionDerivedByYear?: Record<number, boolean>;
  [key: string]:
    | string
    | number
    | null
    | boolean
    | Record<number, number>
    | Record<number, boolean>
    | Record<number, ProductionHistoryPremiumSource>
    | undefined;
};

export type ProductionHistoryGridSummary = ProductionHistoryDashboardGridRow;

export type ProductionHistoryDashboardData = Record<HistoryGranularity, ProductionHistoryDashboardGridRow[]>;

export type ProductionHistoryDashboardDefaults = {
  data: ProductionHistoryDashboardData;
  summaryData: Partial<Record<HistoryGranularity, ProductionHistoryGridSummary | null>>;
};

export const EMPTY_HISTORY_DATA: ProductionHistoryDashboardData = {
  [Granularity.Monthly]: [],
  [Granularity.Quarterly]: [],
  [Granularity.Annual]: [],
};
